import { useEffect, useState } from 'react';
import { operationsApi, type DocumentType } from '../api';

type Props = {
  scope: 'ASSET' | 'FIELD_USER';
  subjectId: string;
  onClose: () => void;
  onUploaded: () => void;
};

const ALLOWED = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_BYTES = 25 * 1024 * 1024;

export function DocumentUploadModal({ scope, subjectId, onClose, onUploaded }: Props) {
  const [types, setTypes] = useState<DocumentType[]>([]);
  const [typeId, setTypeId] = useState('');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    operationsApi.listDocumentTypes(scope).then((rows) => {
      setTypes(rows);
      if (rows.length > 0) setTypeId(rows[0].id);
    });
  }, [scope]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !typeId) return;
    if (!ALLOWED.includes(file.type)) {
      setError('Only PDF, JPG, PNG files are allowed.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File exceeds 25 MB cap.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const ticket = await operationsApi.beginDocumentUpload({
        document_type_id: typeId,
        scope,
        asset_id: scope === 'ASSET' ? subjectId : undefined,
        field_user_id: scope === 'FIELD_USER' ? subjectId : undefined,
        title: title.trim() || file.name,
        mime_type: file.type,
        size_bytes: file.size,
        issued_on: issuedOn ? new Date(issuedOn).toISOString() : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        notes: notes || null,
      });
      // Upload to the signed URL if provided (Supabase Storage). In the local-dev
      // fallback the backend returns an empty url; we still close the modal.
      if (ticket.upload_url) {
        const res = await fetch(ticket.upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!res.ok) {
          throw new Error(`Upload failed: HTTP ${res.status}`);
        }
      }
      onUploaded();
    } catch (e) {
      setError((e as Error).message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={onSubmit} className="card w-full max-w-lg space-y-4 bg-surface p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Upload document</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground">
            ✕
          </button>
        </div>
        {error && (
          <div className="rounded bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{error}</div>
        )}
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Document type *</span>
          <select
            className="input"
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            required
          >
            {types.length === 0 && <option value="">(no types configured)</option>}
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Title</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="(defaults to filename)"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">File *</span>
          <input
            type="file"
            required
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file && (
            <span className="text-xs text-muted-foreground">
              {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type}
            </span>
          )}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs uppercase text-muted-foreground">Issued</span>
            <input
              type="date"
              className="input"
              value={issuedOn}
              onChange={(e) => setIssuedOn(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs uppercase text-muted-foreground">Expires</span>
            <input
              type="date"
              className="input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs uppercase text-muted-foreground">Notes</span>
          <textarea
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={uploading || !file} className="btn-primary">
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </div>
  );
}
